import type React from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Spinner } from '@fluentui/react-components';
import { PersonPasskeyRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'hooks/useAuth';
import { useLoginPageStyles } from './LoginPage.styles';

interface LocationState {
  from?: { pathname: string };
}

/**
 * Login page with Microsoft sign-in button.
 * Redirects to the originally requested page after successful login.
 */
export const LoginPage: React.FC = () => {
  const styles = useLoginPageStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, error, login } = useAuth();

  const state = location.state as LocationState | undefined;
  const from = state?.from?.pathname ?? '/';

  // Redirect to destination if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const handleLogin = async () => {
    await login();
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <PersonPasskeyRegular />
        </div>
        <h1 className={styles.title}>{t('login.title', 'SOC Dashboard')}</h1>
        <p className={styles.subtitle}>
          {t('login.subtitle', 'Sign in with your Microsoft account to continue')}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        {isLoading ? (
          <Spinner size="medium" label={t('login.loading', 'Signing in...')} />
        ) : (
          <Button
            appearance="primary"
            size="large"
            icon={<PersonPasskeyRegular />}
            onClick={handleLogin}
          >
            {t('login.signIn', 'Sign in with Microsoft')}
          </Button>
        )}
      </div>
    </div>
  );
};
